import { useIntl } from 'react-intl';
import { ActionIcon, Checkbox, Flex, Paper, Stack, Text } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import {
  FieldInput,
  PickerInput,
  CheckboxGroupInput,
  ImagePickerInput,
  PickerValue,
} from '../fields/types';
import { ReImportButton } from './ReImportButton';

interface InputListItemProps {
  input: FieldInput;
  onRemove: () => void;
  onUpdate?: (updatedInput: FieldInput) => void;
  diacritics?: string[];
  onDiacriticsChange?: (inputName: string, checked: boolean) => void;
  autoCorrect?: string[];
  onAutoCorrectChange?: (inputName: string, checked: boolean) => void;
}

export const InputListItem = ({
  input,
  onRemove,
  onUpdate,
  diacritics,
  onDiacriticsChange,
  autoCorrect,
  onAutoCorrectChange,
}: InputListItemProps) => {
  const intl = useIntl();

  const handleImagesImported = (
    images: ImagePickerInput['availableImages']
  ) => {
    if (input.type !== 'imagePicker' || !onUpdate) return;

    const imagePickerInput = input as ImagePickerInput;
    const updatedInput: ImagePickerInput = {
      ...imagePickerInput,
      availableImages: images || [],
    };

    onUpdate(updatedInput);
  };

  const renderInputDetails = () => {
    if (input.type === 'picker' || input.type === 'checkboxGroup') {
      const typedInput = input as PickerInput | CheckboxGroupInput;
      return (
        <Text size="xs" c="dimmed">
          {intl.formatMessage({ id: 'options' })}:{' '}
          {typedInput.items?.map((v: PickerValue) => v.label).join(', ')}
        </Text>
      );
    }

    if (input.type === 'imagePicker') {
      const typedInput = input as ImagePickerInput;
      return (
        <Stack gap="xs" mb="xs">
          {typedInput.folderPath && (
            <>
              <Text size="xs" c="dimmed">
                {intl.formatMessage({ id: 'folderPath' })}: <br />{' '}
                <span style={{ fontWeight: 'bold' }}>
                  {typedInput.folderPath}
                </span>
              </Text>
              <ReImportButton
                folderPath={typedInput.folderPath}
                onImagesImported={handleImagesImported}
                size="xs"
                align="left"
              />
            </>
          )}
          <Text size="xs" c="dimmed">
            {intl.formatMessage({ id: 'maxSelection' })}:{' '}
            <span style={{ fontWeight: 'bold' }}>
              {typedInput.maxSelection || 1}
            </span>
          </Text>
          <Text size="xs" c="dimmed">
            {intl.formatMessage({ id: 'totalImportedImages' })}:{' '}
            <span style={{ fontWeight: 'bold' }}>
              {typedInput.availableImages?.length || 0}
            </span>
          </Text>
        </Stack>
      );
    }

    return null;
  };

  return (
    <Paper p="md" withBorder>
      <Flex justify="space-between" align="center" gap="md">
        <Stack gap={4}>
          <Text size="sm">
            {intl.formatMessage({ id: input.type })} - {input.label}
          </Text>
          {renderInputDetails()}
          {input.type === 'imagePicker' && onUpdate && (
            <Checkbox
              label={intl.formatMessage({ id: 'imagesAutoImport' })}
              checked={
                (input as ImagePickerInput).imagesAutoImport !== undefined
                  ? (input as ImagePickerInput).imagesAutoImport
                  : true
              }
              onChange={(event) => {
                const updatedInput = {
                  ...input,
                  imagesAutoImport: event.currentTarget.checked,
                } as ImagePickerInput;
                onUpdate(updatedInput);
              }}
              size="xs"
            />
          )}
          {input.type === 'text' && onDiacriticsChange && (
            <Checkbox
              label={intl.formatMessage({ id: 'diacriticsFields' })}
              checked={(diacritics || []).includes(input.name)}
              onChange={(event) => {
                onDiacriticsChange(input.name, event.currentTarget.checked);
              }}
              size="xs"
            />
          )}
          {input.type === 'textarea' && onAutoCorrectChange && (
            <Checkbox
              label={intl.formatMessage({ id: 'autoCorrectFields' })}
              checked={(autoCorrect || []).includes(input.name)}
              onChange={(event) => {
                onAutoCorrectChange(input.name, event.currentTarget.checked);
              }}
              size="xs"
            />
          )}
        </Stack>
        <ActionIcon variant="default" onClick={onRemove}>
          <IconTrash size={16} />
        </ActionIcon>
      </Flex>
    </Paper>
  );
};
